import { Component, Input, Output, EventEmitter, inject, computed, signal, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonContent, IonActionSheet, PopoverController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { close, share, create, chatbubble, location, time, business, checkmark, pencil, chevronUp, chevronDown, copy, navigate } from 'ionicons/icons';
import { ModalMaterialsSectionComponent } from '../modal-materials-section/modal-materials-section.component';
import { ScheduleComponent } from '../schedule/schedule.component';
import { LeerMasComponent } from '../leer-mas/leer-mas.component';
import { HeaderModalComponent } from '../header-modal/header-modal.component';
import { RecyclingPoint } from '../../../types/recycling-point.interface';
import { CustomModalController } from '../../../shared/services/custom-modal-controller.service';
import { PlatformService } from '../../../shared/services/platform.service';
import { PosthogService } from '../../../shared/services/posthog.service';
import { ScreenLayer } from '../../../shared/enums/screen-layer.enum';
import { mapEventsState } from '../../../state/map-events.state';
import { SwNavigatorService } from '../../../shared/services/sw-navigator.service';
import { PointDataService } from '../../../shared/services/point-data.service';
import { getAddressFromEntity } from '../../../shared/utils/location.utils';
import { ShareService } from '../../../shared/services/share.service';
import { SocialSectionComponent } from '../social-section/social-section.component';
import { ActionButtonsSliderComponent, ActionButton, MODAL_ACTION_BUTTONS } from '../action-buttons-slider/action-buttons-slider.component';
import { ContactInfoComponent, ContactActionEvent } from '../contact-info/contact-info.component';
import { LinksChipsComponent } from '../links-chips/links-chips.component';
import { TrustSourceBadgeComponent } from '../trust-source-badge/trust-source-badge.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { TranslationService } from '../../../shared/services/translation.service';

export interface ModalAction {
  type: 'share' | 'comment' | 'edit' | 'save' | 'contact';
  data?: any;
}

@Component({
  selector: 'app-route-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonContent,
    IonActionSheet,
    ModalMaterialsSectionComponent,
    ScheduleComponent,
    LeerMasComponent,
    HeaderModalComponent,
    SocialSectionComponent,
    ActionButtonsSliderComponent,
    ContactInfoComponent,
    LinksChipsComponent,
    TrustSourceBadgeComponent,
    TranslatePipe
  ],
  templateUrl: './route-modal.component.html',
  styleUrl: './route-modal.component.scss'
})
export class RouteModalComponent implements OnInit, OnChanges {
  private modalCtrl = inject(CustomModalController);
  private platform = inject(PlatformService);
  private swnav = inject(SwNavigatorService);
  private pointDataService = inject(PointDataService);
  private popoverCtrl = inject(PopoverController);
  private posthog = inject(PosthogService);
  private shareService = inject(ShareService);
  private t = inject(TranslationService);

  @Input() route: RecyclingPoint | null = null;
  @Input() scheduleDays: { code: string; label: string; isOpen: boolean }[] = [];
  @Input() scheduleHoursText: string = '';
  @Input() initialHeight?: number;

  @Output() modalAction = new EventEmitter<ModalAction>();
  @Output() dismiss = new EventEmitter<void>();
  @Output() repointMap = new EventEmitter<{ lat: number; lng: number }>();

  // Internal signal to track route changes for computed properties
  private readonly _route$ = signal<RecyclingPoint | null>(null);

  // Input materials (what the route collects)
  readonly inputMaterials = computed(() => {
    const route = this._route$();
    if (!route) return [];
    if (route.materialInput && Array.isArray(route.materialInput) && route.materialInput.length > 0) {
      return route.materialInput;
    }
    if (route.materials && Array.isArray(route.materials) && route.materials.length > 0) {
      return route.materials;
    }
    return [];
  });

  // Output materials (what the route delivers)
  readonly outputMaterials = computed(() => {
    const route = this._route$();
    if (!route) return [];
    if (route.materialOutput && Array.isArray(route.materialOutput) && route.materialOutput.length > 0) {
      return route.materialOutput;
    }
    return [];
  });

  // Route type label (computed to avoid recalculation on every change detection)
  readonly routeTypeLabel = computed(() => {
    const type = this._route$()?.type;
    console.log('getRouteTypeLabel', type);
    switch (type) {
      case 'route':
        return this.t.instant('routeModal.collectionRoute');
      case 'area':
        return this.t.instant('routeModal.collectionArea');
      case 'collection_schedule':
        return this.t.instant('routeModal.collectionSchedule');
      default:
        return this.t.instant('routeModal.defaultRoute');
    }
  });

  // Route icon (computed)
  readonly routeIcon = computed(() => {
    const route = this._route$();
    const type = route?.type || route?.entityType;
    switch (type) {
      case 'route':
        return 'assets/icons/route.svg';
      case 'area':
        return 'assets/icons/route.svg';
      default:
        return 'assets/icons/route.svg';
    }
  });

  // Address string (computed)
  readonly addressString = computed(() => {
    const route = this._route$();
    return getAddressFromEntity(route, { excludeCities: [] });
  });

  // Show edit button when route is missing materials, address, or schedule
  readonly showEditButton = computed(() => {
    const route = this._route$();
    if (!route) return false;
    const hasMaterials = (route.materialInput?.length || 0) > 0
      || (route.materials?.length || 0) > 0;
    const hasAddress = !!(route.address?.street || route.address?.fullAddress);
    const hasSchedule = !!(route.schedules && route.schedules.length > 0);
    return !hasMaterials || !hasAddress || !hasSchedule;
  });

  // Comment state
  showCommentSection: boolean = true;

  // Action buttons state
  actionButtons: ActionButton[] = [...MODAL_ACTION_BUTTONS];

  // Action sheet state
  isActionSheetOpen: boolean = false;
  actionSheetButtons = [
    {
      text: this.t.instant('routeModal.shareLocation'),
      icon: 'location',
      handler: () => this.shareLocation()
    },
    {
      text: this.t.instant('routeModal.shareInfo'),
      icon: 'business',
      handler: () => this.shareInfo()
    },
    {
      text: this.t.instant('routeModal.copyAddress'),
      icon: 'copy',
      handler: () => this.copyAddress()
    },
    {
      text: this.t.instant('common.cancel'),
      role: 'cancel'
    }
  ];

  constructor() {
    addIcons({
      close,
      share,
      create,
      chatbubble,
      location,
      time,
      business,
      checkmark,
      pencil,
      chevronUp,
      chevronDown,
      copy,
      navigate
    });
    // Track modal open
    setTimeout(() => {
      this.posthog.init();
      this.posthog.capture('modal_open', { modal: 'route', routeId: this.route?.id });
    }, 0);
  }

  ngOnInit() {
    // Ensure signal is populated for dynamically created components (Ionic modals)
    // where componentProps might not trigger ngOnChanges on first render
    if (this.route && !this._route$()) {
      this._route$.set(this.route);
    }
  }

  ngOnChanges() {
    // Update internal signal when route input changes
    this._route$.set(this.route);
  }

  // Modal actions
  closeModal() {
    this.posthog.capture('modal_close', { modal: 'route', routeId: this.route?.id });
    this.modalCtrl.dismiss(null, 'close');
  }

  toggleModalHeight() {
    this.posthog.capture('modal_toggle_height', { modal: 'route', routeId: this.route?.id });
    this.modalCtrl.toggleHeight();
  }

  repointMapOnRoute(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    if (!this.route?.position) return;
    
    console.log('🗺️ Repoint button clicked, requesting map repoint via global state');
    
    mapEventsState.requestRecenter({
      lat: this.route.position.lat,
      lng: this.route.position.lng
    });
    
    this.posthog.capture('repoint_map', { routeId: this.route?.id });
  }

  async suggestEdit() {
    if (!this.route) return;

    this.posthog.capture('suggest_edit', { routeId: this.route?.id });

    // Set point data in shared service for edit component
    this.pointDataService.setPointForEdit(this.route);

    this.swnav.navigateObj({ path: `/edit-point/${this.route.id}`, back_path: '/home', layer: ScreenLayer.Edit, typeTransition: 1 });
  }

  // Action buttons handler
  onActionButtonClick(actionId: string) {
    switch (actionId) {
      case 'share':
        this.shareInfo();
        break;
      case 'google':
        this.searchRouteOnGoogle();
        break;
      case 'edit':
        this.suggestEdit();
        break;
      case 'report':
        this.scrollToReports();
        break;
      case 'save':
        this.toggleSave();
        break;
    }
  }

  searchRouteOnGoogle() {
    if (!this.route?.position) return;

    const query = encodeURIComponent(
      this.route.name + ' ' + this.getAddressString()
    );
    const url = `https://www.google.com/search?q=${query}`;

    this.platform.windowOpen(url, '_blank');
    this.posthog.capture('route_google_search', { routeId: this.route?.id });
  }

  scrollToReports() {
    const content = document.querySelector('app-route-modal ion-content');
    const socialSection = document.querySelector('app-route-modal app-social-section');

    if (content && socialSection) {
      socialSection.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Add ui-guide animation after scroll settles
      setTimeout(() => {
        socialSection.classList.add('ui-guide');

        // Remove after animation completes
        setTimeout(() => {
          socialSection.classList.remove('ui-guide');
          socialSection.classList.add('ui-guide-out');

          setTimeout(() => {
            socialSection.classList.remove('ui-guide-out');
          }, 300);
        }, 1000);
      }, 400);
    }

    this.posthog.capture('route_scroll_to_reports', { routeId: this.route?.id });
  }

  toggleSave() {
    const saveButton = this.actionButtons.find(btn => btn.id === 'save');
    if (saveButton) {
      saveButton.active = !saveButton.active;
      this.posthog.capture(saveButton.active ? 'route_save' : 'route_unsave', { routeId: this.route?.id });
    }
  }

  // Contact methods
  hasContactInfo(): boolean {
    if (!this.route) return false;
    return !!(this.route.telefono || this.route.sitio_web || this.route.contactPhone || this.route.contactEmail);
  }

  openWebsite() {
    if (this.route?.sitio_web) {
      this.platform.windowOpen(this.route.sitio_web, '_blank');
      this.posthog.capture('route_website_click', { routeId: this.route?.id });
    }
  }

  callPhone() {
    const phone = this.route?.telefono || this.route?.contactPhone;
    if (phone) {
      this.platform.windowOpen(`tel:${phone}`, '_self');
      this.posthog.capture('route_phone_click', { routeId: this.route?.id });
    }
  }

  // Contact info component handler
  onContactAction(event: ContactActionEvent) {
    switch (event.type) {
      case 'website':
        this.openWebsite();
        break;
      case 'phone':
        this.callPhone();
        break;
      case 'share':
        this.shareInfo();
        break;
    }
  }

  // Share methods
  openShareOptions() {
    this.isActionSheetOpen = true;
    this.posthog.capture('route_share_options_open', { routeId: this.route?.id });
  }

  shareLocation() {
    if (!this.route?.position) return;
    const entity = this.shareService.fromRecyclingPoint(this.route as RecyclingPoint);
    this.shareService.shareLocation(entity);
    this.posthog.capture('route_share_location', { routeId: this.route?.id });
  }

  shareInfo() {
    const entity = this.shareService.fromRecyclingPoint(this.route as RecyclingPoint);
    this.shareService.shareInfo(entity);
    this.posthog.capture('route_share_info', { routeId: this.route?.id });
  }

  copyAddress() {
    const entity = this.shareService.fromRecyclingPoint(this.route as RecyclingPoint);
    this.shareService.copyToClipboard(this.shareService.getAddressString(entity));
    this.posthog.capture('route_copy_address', { routeId: this.route?.id });
  }

  // DEPRECATED: Use addressString() computed signal instead
  getAddressString(): string {
    return this.addressString();
  }

  // DEPRECATED: Use showEditButton() computed signal instead
  shouldShowEditButton(): boolean {
    return this.showEditButton();
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

}
